import type {
  BoardProps,
  ItemsPaletteProps,
} from '@cloudscape-design/board-components';
import { useContainerQuery } from '@cloudscape-design/component-toolkit';
import HelpPanel from '@risk-smart/themed-cloudscape-components/help-panel';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useTools } from '@risksmart-app/components/src/tools/useTools';
import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import type { ActionItem } from 'src/components/actions-button/ActionsButton';
import ActionsButton from 'src/components/actions-button/ActionsButton';
import { useAggregation } from 'src/hooks/useAggregation';
import { useIsModuleEnabledLazy } from 'src/hooks/useIsModuleEnabled';
import { PageLayout } from 'src/layouts';
import { useHasPermissionLazy } from 'src/rbac/useHasPermissionLazy';

import type { ObjectToolbarConfig } from '@/blocks/object-toolbar';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { handleError } from '@/utils/errorUtils';

import { buildDashboardToolbarConfig } from '../buildDashboardToolbarConfig';
import DashboardViewToggle from '../dashboard-view-toggle/DashboardViewToggle';
import { useGetZip } from '../hooks/useGetZip';
import ItemPalette from '../item-palette';
import LayoutBoard from '../layout-board';
import type { StoredWidgetDefinition, WidgetDefinition } from '../types';
import { useDashboardActions, useMyItemsWidgets } from '../useDashboardStore';
import {
  filterWidgetsByUserPermissions,
  getPaletteItems,
} from '../widgets/utils';
import FilterOptions from './FilterOptions';
import MyItemsRibbon from './Ribbon';
import styles from './style.module.scss';
import { getWidgets } from './widgets';

const Dashboard: FC = () => {
  const hasPermission = useHasPermissionLazy();
  const [, setToolsContent] = useTools();
  const isModuleEnabled = useIsModuleEnabledLazy();
  const { t } = useTranslation(['common'], { keyPrefix: 'dashboard' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const myItemsWidgets = useMyItemsWidgets();
  const { setMyItemsWidgets } = useDashboardActions();
  // The filter stays inline on a single row all the way down through tablet
  // widths: as the viewport narrows, the *title* yields first (truncating with
  // an ellipsis) and the filter shrinks, so the dropdown never drops to a second
  // level until genuinely mobile width (CUS-204). Only below this mobile
  // threshold does it stack onto its own full-width row, where an inline filter
  // would be too small to use. Measured on the content area so it also reacts to
  // the side panel.
  const [contentWidth, contentRef] = useContainerQuery(
    (entry) => entry.contentBoxWidth,
    []
  );
  // Below this content-area width the fixed-size header row (264px filter +
  // view-toggle + Actions) can no longer fit even with the title fully
  // truncated, so the filter + view-toggle drop to their own stacked row. Above
  // it everything stays inline and the title truncates to absorb the squeeze.
  const filterStacked = contentWidth !== null && contentWidth < 700;

  const widgets = useMemo(() => getWidgets(), []);

  const { addNotification } = useNotifications();
  const { getZip } = useGetZip();
  const { riskModel } = useAggregation();

  const actionsButtonItems = useMemo<ActionItem[]>(
    () => [
      {
        text: t('actions.addWidget'),
        id: 'add_widget',
        onItemClick: () => setToolsContent('page-content'),
      },
      {
        text: t('actions.clear'),
        id: 'clear',
        onItemClick: () => {
          setMyItemsWidgets([]);
        },
      },
      {
        text: t('actions.exportZip'),
        id: 'export_dashboard_images',
        onItemClick: async () => {
          try {
            addNotification({
              type: 'success',
              content: t('actions.exportZipStarted'),
            });
            await getZip();
          } catch (error) {
            handleError(error);
            addNotification({
              type: 'error',
              content: t('actions.exportZipFailed'),
            });
          }
        },
      },
    ],
    [addNotification, getZip, setMyItemsWidgets, setToolsContent, t]
  );

  // Object-level toolbar (CUS-174) — flag-gated. `headerOverride` replaces the
  // whole PageLayout header, so the dashboard's controls are re-homed onto the
  // toolbar: the actions menu → overflow ⋮, export → the "Download" group, and
  // the stateful FilterOptions + DashboardViewToggle controls are relocated into
  // the toolbar's leading tray via `primaryContent`. The legacy header (below)
  // remains the flag-off fallback and is untouched.
  const toolbarConfig = useMemo<ObjectToolbarConfig>(
    () =>
      buildDashboardToolbarConfig({
        items: actionsButtonItems,
        title: t('my_items_page_title'),
        groupLabel: t('dashboardGroup'),
      }),
    [actionsButtonItems, t]
  );

  const getFilteredWidgets = useCallback(() => {
    const definitions: BoardProps.Item<StoredWidgetDefinition>[] = [];
    for (const widget of filterWidgetsByUserPermissions({
      widgets,
      widgetsToFilter: myItemsWidgets,
      hasPermission,
      riskModel,
      isModuleEnabled,
    })) {
      if (!widgets[widget.widgetType]) {
        handleError(`Unable to find widget with type ${widget.widgetType}`);
        continue;
      }
      const definition = {
        ...widget,
        data: {
          widgetType: widget.widgetType,
          ...widgets[widget.widgetType],
        },
        definition: widgets[widget.widgetType].definition,
      };
      definitions.push(definition);
    }

    return definitions;
  }, [hasPermission, isModuleEnabled, riskModel, myItemsWidgets, widgets]);

  const [paletteItems, setPaletteItems] = useState<
    ItemsPaletteProps.Item<WidgetDefinition>[]
  >(() =>
    getPaletteItems({
      widgets,
      items: getFilteredWidgets(),
      hasPermission,
      riskModel,
      isModuleEnabled,
    })
  );

  useEffect(() => {
    setPaletteItems(
      getPaletteItems({
        widgets,
        items: getFilteredWidgets(),
        hasPermission,
        riskModel,
        isModuleEnabled,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [riskModel, isModuleEnabled]);

  return (
    <PageLayout
      title={t('my_items_page_title')}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar
            config={toolbarConfig}
            // The filter and view toggle sit inline in the toolbar tray at all
            // widths — the tray wraps within itself when cramped (CUS-204), so
            // the filter never drops to a separate banner row.
            primaryContent={
              <>
                <FilterOptions />
                <DashboardViewToggle />
              </>
            }
          />
        ) : undefined
      }
      actions={
        <div className={styles.headerActions}>
          {/* Inline (tablet and up) the whole control set stays on the header
              row — the title truncates with an ellipsis to free space so the
              filter, view-toggle and Actions keep it on one line. At mobile
              width the filter AND the view-toggle drop to the stacked row below
              (see `secondary`); only Actions stays beside the title, so the
              header row still fits with its padding (CUS-204). */}
          {!filterStacked && (
            <>
              <div className={styles.filter}>
                <FilterOptions />
              </div>
              <DashboardViewToggle />
            </>
          )}
          <ActionsButton
            testId={'dashboardMenu'}
            items={actionsButtonItems}
            buttonText={t('actions_button')}
            variant={'normal'}
          />
        </div>
      }
      secondary={
        filterStacked ? (
          <div className={styles.filterRow}>
            <div className={styles.filter}>
              <FilterOptions />
            </div>
            <DashboardViewToggle />
          </div>
        ) : undefined
      }
      panelContent={
        <HelpPanel header={t('side_panel_title')}>
          <ItemPalette items={paletteItems} />
        </HelpPanel>
      }
    >
      <div ref={contentRef}>
        <MyItemsRibbon />
      </div>
      <LayoutBoard
        items={getFilteredWidgets()}
        onAddWidgetClick={() => setToolsContent('page-content')}
        onItemsChanged={(items) => {
          setMyItemsWidgets(
            items.map((item) => {
              const widget = myItemsWidgets.find((w) => item.id === w.id);

              return {
                id: item.id,
                widgetType: item.data.widgetType,
                ...widget,
                rowSpan: item.rowSpan,
                columnSpan: item.columnSpan,
                columnOffset: item.columnOffset,
              };
            })
          );
          setPaletteItems(
            getPaletteItems({
              widgets,
              items,
              hasPermission,
              riskModel,
              isModuleEnabled,
            })
          );
        }}
      />
    </PageLayout>
  );
};

export default Dashboard;
